import { useState, useEffect, useMemo, useRef } from "react";
import { useAtom } from "jotai";
import { Button, Tooltip, Modal, Switch } from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import DeleteForever from "@mui/icons-material/DeleteForever";
import WarningIcon from "@mui/icons-material/Warning";
import dayjs from "dayjs";
import BlockIdSelect from "./BlockIdSelect";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import RolePicker from "./RolePicker";
import ClassAdjustAddInstructor from "./ClassAdjustAddInstructor";
import PropTypes from "prop-types";
import { instanceRowsAtom } from "../../../../components/Atoms/atoms";
import CloseIcon from "@mui/icons-material/Close";
import InfoIcon from "@mui/icons-material/Info";
import AddBoxIcon from "@mui/icons-material/AddBox";
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';

const MAX_COMBINE_CLASS_COUNT = 10;

const style = {
	position: "absolute",
	top: "50%",
	left: "50%",
	transform: "translate(-50%, -50%)",
	width: "90vw",
	height: "98svh",
	bgcolor: "background.paper",
	border: "2px solid #f5f5f5",
	borderRadius: 2,
	display: "flex",
	flexDirection: "column",
};

function getSelectedClassStatusMeta(course) {
	if (!course) {
		return {
			label: "No Course Selected",
			className: "bg-gray-100 text-gray-700 ring-gray-200",
		};
	}

	if (course.evaluate === "Yes") {
		return {
			label: "Submitted",
			className: "bg-primary text-white ring-darkGreen",
		};
	}

	if (course.evaluate === "No") {
		return {
			label: "Excluded",
			className: "bg-red-100 text-red-800 ring-red-200",
		};
	}

	if (course.evaluate === "Pending") {
		return {
			label: "Pending Bulk Submit",
			className: "bg-amber-100 text-amber-800 ring-amber-200",
		};
	}

	if (course.isCombined) {
		return {
			label: "Combined Child",
			className: "bg-rose-100 text-rose-800 ring-rose-200",
		};
	}

	if (course.combine?.length > 0) {
		return {
			label: "Combined Parent",
			className: "bg-blue-100 text-blue-800 ring-blue-200",
		};
	}

	return {
		label: "Not Submitted",
		className: "bg-gray-100 text-gray-700 ring-gray-200",
	};
}

function formatBulkDate(value) {
	if (!value) {
		return "not set";
	}

	return dayjs(value).format("DD/MMM/YY");
}

function toDateInputValue(value) {
	if (!value) {
		return "";
	}

	const parsedValue = dayjs(value);
	return parsedValue.isValid() ? parsedValue.format("YYYY-MM-DD") : "";
}

function SummaryStatCard({ label, value, detail }) {
	return (
		<div className="flex h-full flex-col justify-between rounded-xl border border-gray-200 bg-gray-50 px-3 py-1">
			<p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500">{label}</p>
			<p className=" text-lg font-bold text-gray-900">
				{value} {detail ? <span className=" text-lg font-bold text-gray-900">{detail}</span> : null}
			</p>
		</div>
	);
}

SummaryStatCard.propTypes = {
	detail: PropTypes.node,
	label: PropTypes.string.isRequired,
	value: PropTypes.node,
};

function DateInputField({ label, value, onChange, helper, disabled, min, max, prominent = false }) {
	return (
		<label className={`flex h-full flex-col rounded-xl border px-3 py-1 border-gray-200 bg-white`}>
			<span
				className={`text-[11px] font-semibold uppercase tracking-[0.16em]  ${prominent ? "text-darkGreen" : "text-gray-500"}`}
			>
				{label}
			</span>
			<input
				type="date"
				value={toDateInputValue(value)}
				min={min ? toDateInputValue(min) : undefined}
				max={max ? toDateInputValue(max) : undefined}
				disabled={disabled}
				onChange={(event) => {
					if (!event.target.value) {
						return;
					}

					const parsedValue = dayjs(event.target.value);
					if (parsedValue.isValid()) {
						onChange(parsedValue);
					}
				}}
				className={` rounded-lg border px-3 py-2.5 text-sm font-semibold outline-none transition ${
					disabled
						? "cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400"
						: "border-gray-300 bg-gray-50 text-gray-900 focus:border-blue-500"
				}`}
			/>
			{helper ? (
				<span className={`mt-2 text-xs ${prominent ? "text-darkGreen" : "text-gray-500"}`}>
					{helper}
				</span>
			) : null}
		</label>
	);
}

DateInputField.propTypes = {
	disabled: PropTypes.bool,
	helper: PropTypes.string,
	label: PropTypes.string.isRequired,
	max: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.object, PropTypes.string]),
	min: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.object, PropTypes.string]),
	onChange: PropTypes.func.isRequired,
	prominent: PropTypes.bool,
	value: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.object, PropTypes.string]),
};

function getRoleDisplayName(role) {
	if (!role) {
		return "";
	}

	if (role.name) {
		return role.name;
	}

	return `${role.user?.last_name ?? ""}, ${role.user?.first_name ?? ""}`.trim();
}

function getInstructorSummary(course) {
	return (course?.roles ?? []).map(getRoleDisplayName).filter(Boolean).join(" | ");
}

function countSharedInstructors(rootClass, candidateClass) {
	const selectedEmpIds = new Set((rootClass?.roles ?? []).map((role) => String(role.empid)));
	return (candidateClass?.roles ?? []).filter((role) => selectedEmpIds.has(String(role.empid)))
		.length;
}

function countCommonInstructors(courses) {
	if (!courses?.length) {
		return 0;
	}

	const [firstCourse, ...remainingCourses] = courses;
	const commonInstructorIds = new Set((firstCourse?.roles ?? []).map((role) => String(role.empid)));

	remainingCourses.forEach((course) => {
		const courseInstructorIds = new Set((course?.roles ?? []).map((role) => String(role.empid)));
		Array.from(commonInstructorIds).forEach((empid) => {
			if (!courseInstructorIds.has(empid)) {
				commonInstructorIds.delete(empid);
			}
		});
	});

	return commonInstructorIds.size;
}

function matchesCombineFilter(candidate, filterValue) {
	if (!filterValue.trim()) {
		return true;
	}

	const query = filterValue.toLowerCase();
	return (
		String(candidate.classid).includes(query) ||
		(candidate.ctitle || "").toLowerCase().includes(query) ||
		(candidate.subject_id || "").toLowerCase().includes(query) ||
		(candidate.cnumber || "").toLowerCase().includes(query) ||
		getInstructorSummary(candidate).toLowerCase().includes(query)
	);
}

function buildInstanceRows(selectedClass, rows) {
	if (!selectedClass) {
		return [];
	}

	const rootClassId = selectedClass.original || selectedClass.classid;
	const rowsById = new Map();

	(rows || []).forEach((row) => {
		if (row.classid === rootClassId || row.original === rootClassId) {
			rowsById.set(row.classid, row);
		}
	});

	if (!rowsById.has(selectedClass.classid)) {
		rowsById.set(selectedClass.classid, selectedClass);
	}

	return Array.from(rowsById.values()).sort((a, b) => {
		const aIsRoot = a.classid === rootClassId;
		const bIsRoot = b.classid === rootClassId;

		if (aIsRoot && !bIsRoot) {
			return -1;
		}

		if (!aIsRoot && bIsRoot) {
			return 1;
		}

		return a.classid - b.classid;
	});
}

function CustomTabPanel(props) {
	const { children, value, index, stackBelowXl = false, className = "", ...other } = props;
	const isActive = value === index;
	const visibilityClass = stackBelowXl && !isActive ? "block xl:hidden" : isActive ? "" : "hidden";

	return (
		<div
			role="tabpanel"
			hidden={stackBelowXl ? undefined : !isActive}
			id={`simple-tabpanel-${index}`}
			aria-labelledby={`simple-tab-${index}`}
			className={[className, visibilityClass].filter(Boolean).join(" ")}
			{...other}
		>
			{(stackBelowXl || isActive) && <Box sx={{ p: 0 }}>{children}</Box>}
		</div>
	);
}

CustomTabPanel.propTypes = {
	children: PropTypes.node,
	index: PropTypes.number.isRequired,
	stackBelowXl: PropTypes.bool,
	value: PropTypes.number.isRequired,
};

function a11yProps(index) {
	return {
		id: `simple-tab-${index}`,
		"aria-controls": `simple-tabpanel-${index}`,
	};
}

export default function SelectedClassModal({
	open,
	setOpen,
	selectedClass,
	setSelectedClass,
	onClose,
	handleOpenAddInstancedClass,
	handleSaveAttempt,
	handleSaveChanges,
	validateClass,
	isTermActive,
	isAdmin,
	handleOpenCombineDialogFromModal,
	setUndoDialogOpen,
	setOpenEvaluationDialog,
	setClassForEvaluatedDialog,
	setEvaluationDialogState,
	roles,
	handleChangeRole,
	deleteInstructor,
	addInstructor,
	setAddInstructor,
	rows,
	setRows,
	updateRow,
	allBlockItemsTA,
	handleChangeBlkId,
	unsavedChanges,
	setUnsavedChanges,
	handleDateChangeEndDate,
	handleDateChangeExamDate,
	handleDateChangeSurveyStart,
	handleDateChangeSurveyEnd,
	handleToggleExclude,
	allBlockItems,
	tabValue,
	setTabValue,
}) {
	const selectedClassStatus = getSelectedClassStatusMeta(selectedClass);
	const selectedClassBlockId = selectedClass?.form || "Not set";

	const [instanceRows, setInstanceRows] = useAtom(instanceRowsAtom);
	const [combineSelection, setCombineSelection] = useState([]);
	const [combineFilter, setCombineFilter] = useState("");

	const [blockIdPreviewList, setBlockIdPreviewList] = useState([]);

	const [questionPreviewExpanded, setQuestionPreviewExpanded] = useState(true);

	// Track original state of selectedClass when modal opens
	const originalClassRef = useRef(null);

	// Store original state when modal opens or selectedClass changes
	useEffect(() => {
		if (open && selectedClass && !originalClassRef.current) {
			originalClassRef.current = JSON.parse(JSON.stringify(selectedClass));
		}
		if (!open) {
			originalClassRef.current = null;
		}
	}, [open, selectedClass]);

	// Handle discarding changes - reset to original state without closing modal
	const handleDiscardChanges = () => {
		if (originalClassRef.current) {
			const original = originalClassRef.current;
			// Update the row in the rows array
			updateRow(original.classid, original);
			// Reset selectedClass to original
			setSelectedClass(original);
			// Clear unsaved changes flag
			setUnsavedChanges(false);
			// Update the ref to the current state
			originalClassRef.current = JSON.parse(JSON.stringify(original));
		}
	};

	const handleChange = (event, newValue) => {
		setTabValue(newValue);
	};
	const handleClose = (event, reason) => {
		if (reason && reason === "backdropClick" && unsavedChanges) {
			return;
		} else {
			if (onClose) {
				onClose();
			} else {
				setOpen(false);
			}
		}
	};

	const canUndoCombine = () => {
		const combinedWith = selectedClass?.combine;
		let combinedWithArray = [];
		if (combinedWith?.length > 5) {
			combinedWithArray = combinedWith.split(",");
		} else {
			combinedWithArray = [combinedWith];
		}
		const allClassesExist = combinedWithArray.every((classid) =>
			rows.some((row) => row.classid === Number(classid)),
		);
		return allClassesExist;
	};

	const cannotSubmit =
		validateClass(selectedClass).shouldPrevent && selectedClass?.evaluate !== "No";
	const instructorCount = selectedClass?.roles?.length ?? 0;
	const courseCode = [selectedClass?.subject_id, selectedClass?.cnumber].filter(Boolean).join(" ");
	const courseHasCombineParent = rows.some((row) =>
		row.combine?.includes(String(selectedClass?.classid)),
	);
	const generalEditDisabled =
		selectedClass?.isCombined || (!isTermActive && !isAdmin) || selectedClass?.evaluate === "No";
	const participationDisabled =
		selectedClass?.isCombined || courseHasCombineParent || (!isTermActive && !isAdmin);
	const exclusionDisabled = 
		(selectedClass?.isCombined && selectedClass?.combine) || (!isTermActive && !isAdmin) || selectedClass?.csize <= 4 || instructorCount === 0;

	const combineCandidates = useMemo(() => {
		if (!selectedClass) {
			return [];
		}

		return [...(rows || [])]
			.filter((candidate) => {
				if (candidate.classid === selectedClass.classid) {
					return false;
				}

				if (!candidate.csize || candidate.csize <= 0) {
					return false;
				}

				if (candidate.csPreCombined || candidate.isCombined) {
					return false;
				}

				if (countSharedInstructors(selectedClass, candidate) < 1) {
					return false;
				}

				return true;
			})
			.sort((a, b) => {
				const sharedInstructorDifference =
					countSharedInstructors(selectedClass, b) - countSharedInstructors(selectedClass, a);

				if (sharedInstructorDifference !== 0) {
					return sharedInstructorDifference;
				}

				return a.classid - b.classid;
			});
	}, [rows, selectedClass]);

	const combineCandidatesById = useMemo(
		() => new Map(combineCandidates.map((candidate) => [candidate.classid, candidate])),
		[combineCandidates],
	);

	const filteredCombineCandidates = useMemo(
		() => combineCandidates.filter((candidate) => matchesCombineFilter(candidate, combineFilter)),
		[combineCandidates, combineFilter],
	);

	const combineSelectionRows = useMemo(
		() => combineSelection.map((classid) => combineCandidatesById.get(classid)).filter(Boolean),
		[combineSelection, combineCandidatesById],
	);

	const combineSelectionLimitReached = combineSelection.length >= MAX_COMBINE_CLASS_COUNT - 1;
	const canOpenCombineDialog =
		combineSelectionRows.length > 0 && combineSelectionRows.length + 1 <= MAX_COMBINE_CLASS_COUNT;

	const toggleCombineClass = (classid) => {
		setCombineSelection((prev) => {
			if (prev.includes(classid)) {
				return prev.filter((id) => id !== classid);
			}

			if (prev.length >= MAX_COMBINE_CLASS_COUNT - 1) {
				return prev;
			}

			return [...prev, classid];
		});
	};

	useEffect(() => {
		if (allBlockItems && selectedClass && selectedClass?.form) {
			const items = allBlockItems?.[selectedClass?.form] || [];
			if (items.length > 0) {
				setBlockIdPreviewList(items || []);
			} else {
				setBlockIdPreviewList([]);
			}
		} else {
			setBlockIdPreviewList([]);
		}
	}, [selectedClass, allBlockItems]);

	useEffect(() => {
		if (!selectedClass) {
			setOpen(false);
		}
	}, [selectedClass, setOpen]);

	useEffect(() => {
		setCombineSelection([]);
		setCombineFilter("");
	}, [selectedClass?.classid]);

	useEffect(() => {
		setInstanceRows(buildInstanceRows(selectedClass, rows));
	}, [selectedClass, rows, setInstanceRows]);

	return (
		<Modal
			open={open}
			onClose={handleClose}
			aria-labelledby="modal-modal-title"
			aria-describedby="modal-modal-description"
		>
			<Box sx={style}>
				<div className="shrink-0 border-b-2 px-3 py-2 xl:px-4 xl:pb-4">
					<div className="flex items-start justify-between gap-3 py-1 xl:gap-4 xl:py-2">
						<div className="min-w-0">
							<p className="font-semibold uppercase tracking-[0.12em] text-gray-500 xl:tracking-[0.16em]">
								Manage Course -{" "}
								<span className="text-base font-bold leading-tight text-gray-900 xl:text-xl">
									{selectedClass?.ctitle || "No course selected"} ({selectedClass?.classid ?? "--"})
								</span>
								<span
									className={`ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ring-1 xl:ml-4 xl:px-3 xl:py-1 ${selectedClassStatus.className}`}
								>
									{selectedClassStatus.label}
								</span>
							</p>
							<p className="mt-1 hidden text-sm text-gray-600 xl:block">
								Add or remove instructors, change survey dates, preview question blocks, combine
								another class with this one, or create an instance of this class.
							</p>
						</div>
						<button
							onClick={handleClose}
							className="rounded-md p-1 text-gray-500 transition hover:bg-gray-100 hover:text-gray-700"
						>
							<CloseIcon />
						</button>
					</div>
					<div className="flex flex-col gap-2">
						<div className="hidden flex-wrap items-start justify-end gap-3 xl:flex">
							<div className="flex flex-wrap gap-2">
								{selectedClass?.isCombined ? (
									<span className="inline-flex items-center rounded-full bg-rose-100 px-3 py-1 text-xs font-semibold text-rose-800 ring-1 ring-rose-200">
										Combined Child
									</span>
								) : null}
							</div>
						</div>
						<div className="grid grid-cols-2 gap-2 md:grid-cols-3 xl:grid-cols-6 xl:gap-3">
							<div className="flex h-full flex-col justify-between rounded-lg border border-gray-200 bg-gray-50 px-2 py-1 xl:rounded-xl xl:px-3">
								<p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-gray-500 xl:text-[11px] xl:tracking-[0.16em]">
									Students
								</p>
								<p className="text-sm font-bold text-gray-900 xl:text-lg">
									{selectedClass?.csize ?? "-"}
								</p>
							</div>
							<div className="flex h-full flex-col justify-between rounded-lg border border-gray-200 bg-gray-50 px-2 py-1 xl:rounded-xl xl:px-3">
								<p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-gray-500 xl:text-[11px] xl:tracking-[0.16em]">
									Instructors
								</p>
								<p className="text-sm font-bold text-gray-900 xl:text-lg">
									{selectedClass?.teamsize ?? "-"}
								</p>
							</div>
							<div className="flex h-full flex-col justify-between rounded-lg border border-gray-200 bg-gray-50 px-2 py-1 xl:rounded-xl xl:px-3">
								<p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-gray-500 xl:text-[11px] xl:tracking-[0.16em]">
									Question Block
								</p>
								<p className="truncate text-sm font-bold text-gray-900 xl:text-lg">
									{selectedClassBlockId}
								</p>
							</div>
							<div className="flex h-full flex-col justify-between rounded-lg border border-gray-200 bg-gray-50 px-2 py-1 xl:rounded-xl xl:px-3">
								<p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-gray-500 xl:text-[11px] xl:tracking-[0.16em]">
									Survey Window
								</p>
								<p className="truncate text-sm font-bold text-gray-900 xl:text-lg">
									{formatBulkDate(selectedClass?.survey_start)}{" "}
									<span className="text-sm font-bold text-gray-900 xl:text-lg">
										to {formatBulkDate(selectedClass?.survey_end)}
									</span>
								</p>
							</div>
							<label
								className={`flex h-full flex-col rounded-lg border border-gray-200 bg-white px-2 py-1 xl:rounded-xl xl:px-3
			`}
							>
								<span
									className={`text-[10px] font-semibold uppercase tracking-[0.12em] text-gray-500 xl:text-[11px] xl:tracking-[0.16em] `}
								>
									Class End Date
								</span>
								<input
									type="date"
									value={toDateInputValue(selectedClass?.c_edate)}
									disabled={generalEditDisabled}
									onChange={(event) => {
										if (!event.target.value) {
											return;
										}

										const parsedValue = dayjs(event.target.value);
										if (parsedValue.isValid()) {
											handleDateChangeEndDate(parsedValue);
										}
									}}
									className={`rounded border border-gray-300 bg-gray-50 px-2 text-xs font-semibold text-gray-900 outline-none transition focus:border-blue-500 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-100 disabled:text-gray-400 xl:rounded-lg xl:px-3 xl:text-sm`}
								/>
							</label>
							<label
								className={`flex h-full flex-col rounded-lg border border-gray-200 bg-white px-2 py-1 xl:rounded-xl xl:px-3
			`}
							>
								<span
									className={`text-[10px] font-semibold uppercase tracking-[0.12em] text-gray-500 xl:text-[11px] xl:tracking-[0.16em] `}
								>
									Exam Date
								</span>
								<input
									type="date"
									value={toDateInputValue(selectedClass?.examdate)}
									disabled={generalEditDisabled}
									onChange={(event) => {
										if (!event.target.value) {
											return;
										}

										const parsedValue = dayjs(event.target.value);
										if (parsedValue.isValid()) {
											handleDateChangeExamDate(parsedValue);
										}
									}}
									className={`rounded border border-gray-300 bg-gray-50 px-2 text-xs font-semibold text-gray-900 outline-none transition focus:border-blue-500 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-100 disabled:text-gray-400 xl:rounded-lg xl:px-3 xl:text-sm`}
								/>
							</label>
						</div>
					</div>
				</div>
				<div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-4 py-4">
					<div className="flex flex-col gap-4">
						{selectedClass?.isCombined ? (
							<div className="rounded-xl border-2 border-red-200 bg-red-50 px-5 py-4 text-sm text-red-900">
								<p>
									This class is currently combined with{" "}
									<span className="font-bold">{selectedClass?.combinedWith}</span>.
								</p>
								<p className="mt-2">
									Combined child classes are not surveyed separately, so their survey setup,
									instructors, combining, and instances are read-only here.
								</p>
								<p className="mt-2">
									If you need to change this course, undo the combine on the parent class{" "}
									<span className="font-bold">{selectedClass?.combinedWith}</span> from the
									combining area.
								</p>
							</div>
						) : null}

						<div className="grid gap-4 border-b-2 xl:grid-cols-[minmax(0,1.65fr)_minmax(300px,0.95fr)]">
							<Box
								sx={{ width: "100%", minHeight: "28dvh" }}
								className="min-h-0 flex flex-col rounded-xl bg-white p-1 pb-2"
							>
								<Box sx={{ borderBottom: 1, borderColor: "divider" }} className="hidden shrink-0 xl:block">
									<Tabs
										value={tabValue}
										onChange={handleChange}
										variant="fullWidth"
										aria-label="course management tabs"
									>
										<Tab
											label="Survey Schedule & Question Block"
											{...a11yProps(0)}
											disabled={
												selectedClass?.isCombined ||
												(!isTermActive && !isAdmin) ||
												selectedClass?.evaluate === "No"
											}
										/>
										<Tab
											label="Combining"
											{...a11yProps(1)}
											disabled={
												instructorCount === 0 ||
												selectedClass?.isCombined ||
												(!isTermActive && !isAdmin) ||
												selectedClass?.evaluate === "No"
											}
										/>
									</Tabs>
								</Box>
								<div className="min-h-0 flex-1">
									{selectedClass?.isCombined ? (
										<div className="flex h-full items-center justify-center px-6 text-center text-sm text-gray-600">
											Combining and instance management are available from the parent course after
											the combine is undone.
										</div>
									) : (
										<div className="min-h-0">
											<CustomTabPanel value={tabValue} index={0} stackBelowXl>
												<div className="px-4 py-4">
													<div className="flex flex-col gap-4">
														<div className="flex flex-wrap items-start justify-between border-b pb-4 ">
															<p className="text-[14px] font-semibold uppercase leading-0 w-full text-center tracking-[0.2em] text-darkGreen ">
																<span className="bg-white p-2  ring-gray-200">
																	Survey Schedule AND Question Block
																</span>
															</p>
														</div>

														<div className="grid gap-x-4 gap-y-8 lg:grid-cols-2">
															<DateInputField
																label="Survey Start Date"
																value={selectedClass?.survey_start}
																min={dayjs().add(1, "day")}
																max={
																	selectedClass?.examdate
																		? dayjs(selectedClass?.examdate).subtract(1, "day")
																		: null
																}
																onChange={handleDateChangeSurveyStart}
																disabled={generalEditDisabled}
																helper="Changing this will also set a survey end date based on the exam date."
																prominent
															/>
															<DateInputField
																label="Survey End Date"
																value={selectedClass?.survey_end}
																min={
																	selectedClass?.survey_start
																		? dayjs(selectedClass?.survey_start).add(1, "day")
																		: null
																}
																max={
																	selectedClass?.examdate
																		? dayjs(selectedClass?.examdate).subtract(1, "day")
																		: null
																}
																onChange={handleDateChangeSurveyEnd}
																disabled={generalEditDisabled}
																prominent
															/>
															<div className="flex flex-col gap-4 xl:col-span-2 h-full">
																<div className="rounded-xl border border-gray-200 bg-white px-4 py-2 h-full">
																	<div className="flex flex-col gap-4 h-full">
																		<p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-darkGreen">
																			Question Block
																		</p>

																		<BlockIdSelect
																			disabled={generalEditDisabled}
																			blockIds={allBlockItemsTA}
																			value={selectedClass?.form ? selectedClass?.form : ""}
																			onChange={(event) => handleChangeBlkId(event)}
																			width="100%"
																			marginTop="0px"
																			marginBottom="0px"
																			fontSize="14px"
																			fontWeight="600"
																		/>

																		<div className="h-full">
																			{/* <button
															className=" text-[11px] font-semibold uppercase tracking-[0.16em] text-darkGreen underline"
															onClick={() => setQuestionPreviewExpanded((prev) => !prev)}
														>
															{questionPreviewExpanded
																? "Click to Collapse"
																: "Click to Preview Questions"}
														</button> */}
																			{questionPreviewExpanded ? (
																				blockIdPreviewList.length > 0 ? (
																					<ul className="flex max-h-[30dvh] h-full flex-col gap-1 overflow-y-auto border-y-2 pr-4">
																						{blockIdPreviewList.map((item) => (
																							<li
																								key={item.item_id}
																								className="flex gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm"
																							>
																								<p className="shrink-0 font-semibold text-gray-500">
																									{item.itemno})
																								</p>
																								<p>{item.text}</p>
																							</li>
																						))}
																					</ul>
																				) : (
																					<div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-sm text-gray-500">
																						No preview is available for the current block selection.
																					</div>
																				)
																			) : null}
																		</div>
																	</div>
																</div>
															</div>
														</div>
													</div>
												</div>
											</CustomTabPanel>
											<CustomTabPanel value={tabValue} index={1} stackBelowXl>
												<div className="flex min-h-0 w-full flex-col px-2 py-3">
													{selectedClass?.combine?.length > 0 ? (
														<div className="flex w-full max-w-2xl flex-col gap-3 self-center rounded-xl border p-6 xl:p-12">
															<div className="px-1">
																<p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500">
																	Existing Combination
																</p>
																<p className="mt-1 text-sm text-gray-600">
																	{selectedClass?.ctitle} ({selectedClass?.classid}) is currently
																	combined with:
																</p>
															</div>
															<div className="grid grid-cols-2 gap-2 rounded-lg border border-gray-200 bg-gray-50 p-4 sm:grid-cols-3">
																{selectedClass?.combine?.length > 0 &&
																	selectedClass?.combine.split(",").map((combine) => (
																		<p key={combine} className="font-bold text-gray-900">
																			{combine}
																		</p>
																	))}
															</div>
															{canUndoCombine() && (
																<button
																	onClick={(event) => setUndoDialogOpen(true)}
																	disabled={unsavedChanges}
																	className={`w-full self-end rounded-md border-2 px-2 py-2 text-center text-sm font-semibold shadow-md ${
																		canUndoCombine()
																			? "border-red-700 bg-red-600 text-white hover:border-red-800 hover:bg-red-700 hover:ring-2 hover:ring-gray-300"
																			: "border-gray-400 bg-gray-300 text-gray-400 hover:cursor-not-allowed"
																	} disabled:border-gray-400 disabled:bg-gray-300 disabled:text-gray-400 disabled:hover:cursor-not-allowed disabled:hover:ring-0`}
																>
																	Undo Combine
																</button>
															)}

															{selectedClass?.csPreCombined && (
																<p className="flex flex-col gap-1 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
																	Class is combined from BearTracks/Campus Solutions. This cannot be
																	undone.
																	<span className="text-green-700">
																		If you would like to further combine this course, select another
																		course taught by the same instructor(s) to start and combine
																		normally.
																	</span>
																</p>
															)}
														</div>
													) : (
														<div className="flex min-h-0 flex-col gap-4 px-1 py-2">
															<div className="flex flex-wrap items-start justify-between border-b pb-4 ">
																<p className="text-[14px] font-semibold uppercase leading-0 w-full text-center tracking-[0.2em] text-darkGreen ">
																	<span className="bg-white p-2  ring-gray-200">
																		Combining Courses
																	</span>
																</p>
															</div>
															<div className="grid gap-4 xl:grid-cols-[minmax(250px,0.95fr)_minmax(0,1.35fr)]">
																<div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm space-y-4">
																	<div>
																		<p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500">
																			Root Class
																		</p>
																		<p className="mt-1 text-sm font-bold text-gray-900">
																			{selectedClass?.ctitle}
																		</p>
																		{selectedClass?.classid && (
																			<p className="text-xs text-gray-500">
																				Class ID: {selectedClass?.classid}
																			</p>
																		)}
																		<p className="mt-2 text-xs text-gray-600">
																			{getInstructorSummary(selectedClass) ||
																				"No instructors assigned"}
																		</p>
																	</div>

																	<div className="rounded-md border border-gray-200 bg-white px-3 py-3">
																		<p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500">
																			Selected For Combine ({combineSelectionRows.length})
																		</p>
																		{combineSelectionRows.length > 0 ? (
																			<ul className="mt-2 max-h-44 space-y-1 overflow-y-auto pr-1">
																				{combineSelectionRows.map((combiningClass) => (
																					<li
																						key={combiningClass.classid}
																						className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs"
																					>
																						<p className="font-semibold text-gray-900">
																							{combiningClass.ctitle}
																						</p>
																						<p className="text-[11px] text-gray-500">
																							Class ID: {combiningClass.classid}
																						</p>
																					</li>
																				))}
																			</ul>
																		) : (
																			<div className="mt-2 space-y-1 text-xs text-gray-600">
																				<p className="font-semibold">No classes selected yet.</p>
																				<p>
																					Choose candidate courses from the list to the right, then
																					continue to the combine setup step.
																				</p>
																			</div>
																		)}
																	</div>

																	<div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-3 text-xs text-blue-900">
																		Candidates are sorted by shared instructors to match the Edit
																		Classes workflow.
																		{combineSelectionLimitReached && (
																			<p className="mt-1 font-semibold text-amber-700">
																				Selection limit reached. A maximum of{" "}
																				{MAX_COMBINE_CLASS_COUNT} classes can be combined at once,
																				including the root course.
																			</p>
																		)}
																	</div>
																</div>

																<div className="flex min-h-0 flex-col gap-3">
																	<div className="flex gap-2">
																		<input
																			type="text"
																			placeholder="Search for classes by class ID, subject, number, title, or instructor..."
																			value={combineFilter}
																			onChange={(event) => setCombineFilter(event.target.value)}
																			className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 outline-none ring-0 transition focus:border-blue-500"
																		/>
																		{combineFilter && (
																			<button
																				onClick={() => setCombineFilter("")}
																				className="rounded-md border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-100"
																			>
																				Clear
																			</button>
																		)}
																	</div>

																	<div className="min-h-0 overflow-hidden rounded-lg border border-gray-200 bg-white">
																		<div className="max-h-[320px] overflow-y-auto">
																			<table className="w-full text-sm">
																				<thead className="sticky top-0 bg-gray-100">
																					<tr>
																						<th className="w-12 p-2 text-left"></th>
																						<th className="w-24 p-2 text-left">Class ID</th>
																						<th className="p-2 text-left">Course</th>
																						<th className="w-24 p-2 text-center">Shared</th>
																						<th className="w-20 p-2 text-right">Size</th>
																					</tr>
																				</thead>
																				<tbody>
																					{filteredCombineCandidates.length === 0 ? (
																						<tr>
																							<td
																								colSpan={5}
																								className="p-4 text-center text-sm text-gray-500"
																							>
																								No eligible classes found.
																							</td>
																						</tr>
																					) : (
																						filteredCombineCandidates.map((candidate) => {
																							const isSelected = combineSelection.includes(
																								candidate.classid,
																							);
																							const hasCommonInstructorPath =
																								countCommonInstructors([
																									selectedClass,
																									...combineSelectionRows.filter(
																										(course) =>
																											course.classid !== candidate.classid,
																									),
																									candidate,
																								]) > 0;
																							const disableSelection =
																								(combineSelectionLimitReached && !isSelected) ||
																								(!isSelected && !hasCommonInstructorPath);
																							const sharedInstructorCount = countSharedInstructors(
																								selectedClass,
																								candidate,
																							);

																							return (
																								<tr
																									key={candidate.classid}
																									onClick={() => {
																										if (!disableSelection) {
																											toggleCombineClass(candidate.classid);
																										}
																									}}
																									className={`border-t transition ${
																										disableSelection
																											? "cursor-not-allowed opacity-60"
																											: "cursor-pointer hover:bg-gray-50"
																									}`}
																									title={
																										disableSelection && !hasCommonInstructorPath
																											? "This selection would leave no instructors shared across the full combine set."
																											: undefined
																									}
																								>
																									<td className="p-2 align-top">
																										<input
																											type="checkbox"
																											readOnly
																											checked={isSelected}
																											disabled={disableSelection}
																										/>
																									</td>
																									<td className="p-2 align-top font-semibold text-gray-900">
																										{candidate.classid}
																									</td>
																									<td className="p-2 align-top">
																										<p
																											className="truncate font-medium text-gray-900"
																											title={candidate.ctitle}
																										>
																											{candidate.ctitle}
																										</p>
																										<p
																											className="truncate text-[11px] text-gray-500"
																											title={getInstructorSummary(candidate)}
																										>
																											{getInstructorSummary(candidate) ||
																												"No instructors assigned"}
																										</p>
																									</td>
																									<td className="p-2 text-center align-top text-gray-700">
																										{sharedInstructorCount}
																									</td>
																									<td className="p-2 text-right align-top text-gray-700">
																										{candidate.csize ?? "-"}
																									</td>
																								</tr>
																							);
																						})
																					)}
																				</tbody>
																			</table>
																		</div>
																	</div>
																</div>
															</div>

															<div className="flex flex-col gap-2 sm:flex-row">
																<button
																	onClick={() =>
																		handleOpenCombineDialogFromModal?.(combineSelectionRows)
																	}
																	disabled={(!isTermActive && !isAdmin) || !canOpenCombineDialog}
																	className="w-full rounded-md border-2 border-green-900 bg-green-800 px-2 py-2 text-sm font-semibold text-white shadow-md transition hover:border-green-950 hover:bg-green-900 hover:ring-2 hover:ring-gray-300 disabled:border-gray-400 disabled:bg-gray-400 disabled:text-gray-100 disabled:hover:ring-0"
																>
																	Continue to Combine Setup ({combineSelectionRows.length + 1})
																</button>
																<button
																	onClick={() => setCombineSelection([])}
																	disabled={combineSelection.length === 0}
																	className="w-full rounded-md border-2 border-gray-300 bg-white px-2 py-2 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400"
																>
																	Clear Selection
																</button>
															</div>
														</div>
													)}
												</div>
											</CustomTabPanel>
										</div>
									)}
								</div>
							</Box>

							<div className="border-t-2 pt-4 xl:border-l-2 xl:border-t-0 xl:px-8">
								<div className="flex min-h-0 flex-col gap-4">
									<div className="flex flex-wrap items-start justify-between gap-3">
										<div className="flex gap-2 items-center justify-center w-full">
											<p className="text-[14px] font-semibold uppercase leading-0 w-full text-center tracking-[0.2em] text-darkGreen flex items-center justify-center gap-2">
												<span className="bg-white p-2  ring-gray-200">Instructors</span>
												<Tooltip
													title="Add or remove instructors from this course. At least one instructor must be assigned to submit the course."
													placement="top"
												>
													<InfoIcon
														size="small"
														color="primary"
														sx={{ width: 20, height: 20 }}
														className="cursor-pointer"
													/>
												</Tooltip>
											</p>
										</div>
									</div>

									<div className="grid gap-2 xl:grid-cols-2">
										<div className="  w-full col-span-2">
											{addInstructor && !generalEditDisabled ? (
												<div className="border px-2 py-2 ">
													<ClassAdjustAddInstructor
														rows={rows}
														setRows={setRows}
														setAddInstructor={setAddInstructor}
													/>
												</div>
											) : (
												<button
													onClick={() => setAddInstructor(true)}
													disabled={generalEditDisabled}
													className="w-full rounded-xl hover:bg-primary/80 bg-primary py-2 text-center text-sm font-semibold text-white shadow-md transition  hover:bg-green-800   disabled:bg-gray-400 "
												>
													<PersonAddIcon /> Add Instructor
												</button>
											)}
										</div>

										<div className="col-span-2 max-h-[50dvh] min-h-[220px] overflow-y-auto border p-4 shadow-md">
											{instructorCount > 0 ? (
												<ol className="flex flex-col gap-2">
													{selectedClass?.roles?.map((role) => {
														const roleDisplayName =
															getRoleDisplayName(role) || "Unknown instructor";

														return (
															<li
																key={role.roleid || role.empid || role.ccid || roleDisplayName}
																className="flex w-full items-center justify-between gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 shadow-sm xl:flex-col xl:items-stretch"
															>
																<p
																	className="min-w-0 w-1/2 truncate text-sm font-semibold text-gray-900 xl:w-full"
																	title={roleDisplayName}
																>
																	{roleDisplayName}
																</p>
																<div className="flex min-w-0 w-1/2 items-center justify-between gap-2 xl:w-full">
																	<div className="flex min-w-0 items-center rounded-md py-1">
																		<div className="uppercase  text-[10pt] border-l border-t border-b rounded-l-xl border-r px-1 py-1.5">
																			Role
																		</div>
																		<div className="min-w-0 flex-1 overflow-hidden rounded-r-xl border-b border-r border-t bg-white px-1 py-[1.5px]">
																			<RolePicker
																				disabled={generalEditDisabled}
																				list={roles}
																				label="Role"
																				className=" "
																				width="100%"
																				size="small"
																				marginBottom="0px"
																				value={role.class_role || ""}
																				onChange={(event) =>
																					handleChangeRole(event, role, selectedClass)
																				}
																			/>
																		</div>
																	</div>
																	<Button
																		variant="outlined"
																		color="error"
																		endIcon={<PersonRemoveIcon />}
																		onClick={(event) => deleteInstructor(event, role)}
																		disabled={generalEditDisabled}
																		className="flex items-center justify-center rounded-md text-red-700 transition bg-red-50  hover:bg-red-200 hover:text-red-900 disabled:cursor-not-allowed disabled:text-gray-300 disabled:hover:bg-transparent"
																		sx={{
																			minWidth: 0,
																			"@media (min-width:1280px)": {
																				minWidth: 96,
																			},
																		}}
																	>
																		Remove 
																	</Button>
																</div>
															</li>
														);
													})}
												</ol>
											) : (
												<div className="flex min-h-[170px] items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 py-10 text-center text-sm text-gray-600">
													No instructors are assigned to this course yet.
												</div>
											)}
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div className="shrink-0 border-t px-4 py-4">
					<div
						className="flex flex-wrap items-center justify-between gap-3"
						onClick={handleSaveAttempt}
					>
						<div className="flex flex-wrap gap-3">
							{(selectedClass?.evaluate === "Yes" ||
								selectedClass?.evaluate === "No" ||
								(selectedClass?.evaluate === "" && selectedClass?.wasExcluded)) && (
								<Tooltip
									title={
										courseHasCombineParent &&
										selectedClass?.evaluate === "No" &&
										"Cannot delete exclusion for a child of a combined course."
									}
								>
									<span
										className={`${
											courseHasCombineParent && selectedClass?.evaluate === "No"
												? "bg-orange-400/20 border border-orange-400/20 rounded-md cursor-not-allowed"
												: ""
										}`}
									>
										<Button
											variant="outlined"
											color="error"
											disabled={
												unsavedChanges || (!isTermActive && !isAdmin) || courseHasCombineParent
											}
											onClick={() => {
												setClassForEvaluatedDialog(selectedClass);
												setEvaluationDialogState("evaluated");
												setOpenEvaluationDialog(true);
											}}
											className="flex items-center gap-1"
										>
											{selectedClass?.evaluate === "No" ? "Delete Exclusion" : "Delete Survey Submission"}
											<DeleteForever />
										</Button>
									</span>
								</Tooltip>
							)}
							<button
								disabled={(!isTermActive && !isAdmin) || selectedClass?.isCombined}
								onClick={handleOpenAddInstancedClass}
								className=" rounded  bg-green-700 px-2 py-2 text-center text-sm font-medium text-white shadow-md hover:border-green-900 hover:bg-green-800 hover:ring-2 hover:ring-gray-300 disabled:border-gray-500 disabled:bg-gray-400"
							>
								<AddBoxIcon size="small" color="inherit" /> Create Instanced Class
							</button>
						</div>

						<div className="ml-auto flex gap-2 ">
							<div
								className={`flex items-center justify-between gap-4 rounded-xl border px-4  ${
									selectedClass?.evaluate === "No"
										? "border-red-200 bg-red-50"
										: "border-gray-200 bg-gray-50"
								}`}
							>
								<div>
									<p className="text-sm font-semibold text-gray-900">
										<Tooltip
											title={
												participationDisabled || exclusionDisabled
													? "Cannot modify exclusion status while course is combined or other restrictions apply. Course must meet basic SPOT submission requirements, even for exclusion. If you cannot submit a course for exclusion it also cannot be submitted for surveying, so excluding it is redundant."
													: "Use this only when the course should not be surveyed. Courses that are never submitted are excluded automatically, so this is primarily designed to help you avoid accidental duplicate submissions for combined courses or bulk submissions."
											}
											placement="top"
										>
											<span className="underline text-primary cursor-help">Exclude</span>
										</Tooltip>{" "}
										from SPOT
									</p>
								</div>
								<Switch
									checked={selectedClass?.evaluate === "No"}
									onChange={(event) => handleToggleExclude(event.target.checked)}
									disabled={participationDisabled || exclusionDisabled}
									color="error"
								/>
							</div>
							<Button
								disabled={!unsavedChanges || (!isTermActive && !isAdmin)}
								variant="outlined"
								color="error"
								onClick={handleDiscardChanges}
								className="flex items-center gap-1"
							>
								Discard Current Changes
								<DeleteForever />
							</Button>
							{cannotSubmit ? (
								<Tooltip title={validateClass(selectedClass).message} placement="bottom">
									<Button
										variant="contained"
										disabled={!isTermActive && !isAdmin}
										color="warning"
										className="flex min-w-[220px] items-center gap-1"
									>
										<WarningIcon />
										Resolve Issue Before Save
									</Button>
								</Tooltip>
							) : (
								<Button
									variant="contained"
									onClick={handleSaveChanges}
									disabled={
										(!isTermActive && !isAdmin) ||
										(!unsavedChanges && selectedClass?.evaluate === "Yes") ||
										(!unsavedChanges && selectedClass?.evaluate === "No")
									}
									className="flex min-w-[220px] items-center gap-1"
								>
									{!unsavedChanges ? "Submit SPOT Survey" : "Save Changes"}
									<SaveIcon />
								</Button>
							)}
						</div>
					</div>
				</div>
			</Box>
		</Modal>
	);
}
